import { ref } from "vue"

// Sentry's own answer to a sent report is a pill in a corner that is gone after five seconds, and while
// it sends, the form only dims its buttons. People were left wondering whether a report had arrived at
// all. So the app answers instead: the moment the report goes out, LewFeedbackSentDialog opens with a
// spinner, Sentry's form steps aside, and once Sentry has accepted the report the spinner turns into a
// check and the dialog says what happens next. Its Vorgangsnummer is also the `reference` tag on the
// feedback event, so a support mail quoting it finds the report (`reference:3F9A1C27`). If sending
// fails, the dialog closes and the form comes back with Sentry's message.
//
// Like the priority select, this leans on the SDK's markup (the `#sentry-feedback` shadow host and its
// `.dialog`, `success__position`, `form__error-container`). If an update changes it, the form stays
// visible behind the dialog while sending and the pill shows after it — worth checking after bumping
// @sentry/vue.

/** The report on its way or just sent (`status` "sending" | "sent"), while the dialog about it is open. */
export const feedbackReport = ref(null)

/** An answer that comes back at once would only flash the spinner; it shows at least this long. */
const MIN_SENDING_MS = 700

let sendingSince = 0

/** How long the spinner still has to run before the answer, either one, may show. */
export const sendingRemainder = () => Math.max(0, MIN_SENDING_MS - (Date.now() - sendingSince))

/** What the person is shown and what the tag holds: one function, so the two cannot drift apart. */
const referenceOf = eventId => eventId.slice(0, 8).toUpperCase()

// A version 4 UUID as 32 hex digits, the shape of the SDK's own ids. Not `crypto.randomUUID`: it only
// exists in a secure context, and an exception in `beginSending` would stop the report from being sent.
function newEventId() {
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  return Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("")
}

const host = () => document.getElementById("sentry-feedback")

const STYLE_ID = "lew-feedback-style"

const STYLE = `
:host([data-sending]) .dialog {
  visibility: hidden;
}
.success__position {
  display: none;
}
/* A send error is one long sentence; without this the form widens to fit it on one line. */
.form__error-container {
  width: 0;
  min-width: 100%;
}
`

// `feedbackIntegration({ onFormOpen })`: runs right after the form has rendered.
export function addConfirmationStyle() {
  const shadow = host()?.shadowRoot
  if (!shadow) return
  let style = shadow.getElementById(STYLE_ID)
  if (!style) {
    style = document.createElement("style")
    style.id = STYLE_ID
    style.textContent = STYLE
  }
  // Moved behind the styles of a form created since, so ties go to these rules.
  shadow.append(style)
}

// `client.on("beforeSendFeedback")`: fires once the form has passed its checks and the report is about
// to go out, but before the SDK gives the event its id. So the id is chosen here, where the tag can be
// taken from it; `scope.captureEvent` keeps an id the event already has and returns it, which is how
// the same id reaches `confirmSent`.
export function beginSending(event) {
  event.event_id ||= newEventId()
  const reference = referenceOf(event.event_id)
  event.tags = { ...event.tags, reference }
  host()?.toggleAttribute("data-sending", true)
  sendingSince = Date.now()
  feedbackReport.value = { status: "sending", reference }
}

// `feedbackIntegration({ onSubmitSuccess })`: Sentry has answered with a 2xx.
export function confirmSent(_data, eventId) {
  host()?.toggleAttribute("data-sending", false)
  feedbackReport.value = { status: "sent", reference: referenceOf(eventId) }
}

// `feedbackIntegration({ onSubmitError })`: the form shows why, so it has to be the one on screen. Without
// a network that is a few milliseconds after `beginSending`; the form stays hidden behind the spinner
// meanwhile, so nothing can be sent again before it is back.
export function abandonSending() {
  setTimeout(() => {
    host()?.toggleAttribute("data-sending", false)
    feedbackReport.value = null
  }, sendingRemainder())
}
